import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
} from '@angular/core';
import { isDefined } from '@sama/js-core';
import { DirectiveSuperclass } from '@sama/ng-core';
import { isUndefined, pickBy, size } from 'lodash';
import { merge, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TaskWorkspaceStore } from '../../../../../../core/h3ml/state/task-workspace-store';
import { SelectionService } from '../../../../../../core/outputs/annotation/annotation-shared/utils/selection.service';
import { RootKeyboardShortcutService } from '../../../../../../core/shared/keyboard-shortcuts/root-keyboard-shortcut.service';
import { ObjectScopingService } from '../../../../../../core/shared/misc-services/object-scoping.service';
import { ShapeService } from '../../../../../../core/shared/misc-services/shape.service';
import { isShapeState } from '../../../../../../cross-team-shared/annotation/shared/state/shape-state';
import { keys } from '../../../../../../cross-team-shared/misc-utils/keys';
import { getOrThrow } from '../../../../../../cross-team-shared/misc-utils/strict-record';
import { AnnotationListService } from './annotation-list.service';
import { ObjectDragAndDropService } from './object-drag-and-drop.service';

@Component({
  selector: 'h3-annotation-list',
  templateUrl: './annotation-list.component.html',
  styleUrls: ['./annotation-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AnnotationListComponent
  extends DirectiveSuperclass
  implements OnInit
{
  private readonly annotationListService = inject(AnnotationListService);
  showAttachedObjectsHeader$!: Observable<boolean>;
  scopedObjectId$ = this.taskWorkspaceStore('scopedObjectId').$;
  skipDragAnimation$ = inject(ObjectDragAndDropService).canDrop$();
  shapeItems$ = this.annotationListService.getShapes$();

  constructor(
    private taskWorkspaceStore: TaskWorkspaceStore,
    private keyboardShortcutService: RootKeyboardShortcutService,
    private shapeService: ShapeService,
    private selectionService: SelectionService,
    private objectScopingService: ObjectScopingService,
  ) {
    super();
  }

  ngOnInit() {
    this.showAttachedObjectsHeader$ = this.scopedObjectId$.pipe(
      map(
        (scopedObjectId) =>
          isDefined(scopedObjectId) && !this.taskWorkspaceStore.isUsingGenAi(),
      ),
    );

    this.setUpScopeObjectSubscription();
    this.setSubscriptionToSelectAllKeyboardEvents();
    this.setUpSelectNextAnnotationSubscription();
  }

  private setUpSelectNextAnnotationSubscription() {
    this.subscribeTo(
      merge(
        this.keyboardShortcutService.getEvent$('selectNextAnnotation'),
        this.keyboardShortcutService.getEvent$('selectPreviousAnnotation'),
      ),
      ({ keyboardEvent, shortcutName }) => {
        keyboardEvent.preventDefault();
        const direction =
          shortcutName === 'selectNextAnnotation' ? 'forward' : 'backward';
        const nextItem =
          this.annotationListService.getNextAnnotationItem(direction);
        if (isDefined(nextItem)) {
          const selections: [number[], number[]] = isShapeState(nextItem)
            ? [[], [nextItem.index]]
            : [[nextItem.index], []];

          this.selectionService.setSelection(...selections, {
            source: 'object_list_TAB',
          });
        }
      },
    );
  }

  private setUpScopeObjectSubscription() {
    this.subscribeTo(
      this.keyboardShortcutService.getEvent$('scopeObject'),
      () => {
        const objectIdToScope = this.getFocusedObjectId();

        if (isUndefined(objectIdToScope)) {
          return;
        }

        this.objectScopingService.scopeObject(
          objectIdToScope,
          'keyboard_shortcut',
          'scope',
        );
        this.selectionService.selectFirstItemInObject(objectIdToScope);
      },
    );
  }

  private getFocusedObjectId() {
    const { objectSelection, scopedObjectId, objects } =
      this.taskWorkspaceStore.state();

    const selectedShapes = this.shapeService.getShapes(
      keys(this.taskWorkspaceStore.state().shapeSelection),
    );
    const selectedShapesInScopedObject = selectedShapes.filter(
      (shape) => shape.parentId === scopedObjectId,
    );

    const scopedObjectSelection = pickBy(
      objectSelection,
      (_, objectId) =>
        getOrThrow(objects, Number(objectId)).parentId === scopedObjectId,
    );

    if (
      size(selectedShapesInScopedObject) > 0 ||
      size(scopedObjectSelection) !== 1
    ) {
      return undefined;
    }

    return keys(scopedObjectSelection)[0];
  }

  private setSubscriptionToSelectAllKeyboardEvents() {
    this.subscribeTo(
      this.keyboardShortcutService.getEvent$('selectAllShapes'),
      ({ keyboardEvent, eventTrackingValue }) => {
        // so ctrl+a does not select the text on the page
        keyboardEvent.preventDefault();
        const { scopedObjectId, objects } = this.taskWorkspaceStore.state();
        let objectIds = keys(objects);
        let shapeIndexes: number[] = [];
        if (isDefined(scopedObjectId)) {
          const scopedObject = getOrThrow(objects, scopedObjectId);
          shapeIndexes = scopedObject.childShapeIds;
          objectIds = scopedObject.childObjectIds;
        }
        this.selectionService.setSelection(
          objectIds,
          shapeIndexes,
          eventTrackingValue,
        );
      },
    );
  }
}
